import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import logo from "@/assets/logo_medium.png";
import { supabase } from "@/integrations/supabase/client";
import { loadTurnstileScript, verifyTurnstileToken, getTurnstileSiteKey } from "@/utils/turnstile";

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: { sitekey: string; size?: string; theme?: "light" | "dark" | "auto"; callback?: (token: string) => void; "error-callback"?: () => void }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);

  // Carregar script do Turnstile
  useEffect(() => {
    const siteKey = getTurnstileSiteKey();
    console.log("🔍 Turnstile - Site Key:", siteKey ? "✅ Configurada" : "❌ Não configurada");
    
    if (!siteKey) {
      console.warn("⚠️ Turnstile site key não configurada. O widget não será exibido.");
      return;
    }

    const renderWidget = () => {
      if (!turnstileRef.current) {
        console.error("❌ Turnstile - Elemento não encontrado");
        return;
      }

      if (!window.turnstile) {
        console.error("❌ Turnstile - API não disponível");
        return;
      }

      try {
        console.log("🔄 Turnstile - Renderizando widget...");
        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          size: "normal",
          theme: "light",
          callback: (token: string) => {
            console.log("✅ Turnstile - Token gerado");
            turnstileTokenRef.current = token;
          },
          "error-callback": () => {
            console.error("❌ Turnstile - Erro no widget (error-callback)");
            turnstileTokenRef.current = null;
            toast.error("Erro na verificação de segurança. Verifique se o domínio está configurado corretamente no Cloudflare.");
          },
        });
        turnstileWidgetId.current = widgetId;
        console.log("✅ Turnstile - Widget renderizado com sucesso, ID:", widgetId);
      } catch (error) {
        console.error("❌ Turnstile - Erro ao renderizar:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes("110200")) {
          toast.error("Erro na configuração do Turnstile. Verifique se o domínio está autorizado no Cloudflare.");
        }
      }
    };

    loadTurnstileScript()
      .then(() => {
        console.log("✅ Turnstile - Script carregado");
        // Tentar renderizar imediatamente
        renderWidget();
        
        // Se não funcionou, tentar novamente após um delay
        if (!turnstileWidgetId.current) {
          setTimeout(() => {
            console.log("🔄 Turnstile - Tentando renderizar novamente...");
            renderWidget();
          }, 500);
        }
      })
      .catch((error) => {
        console.error("❌ Erro ao carregar Turnstile:", error);
      });

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, []);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);

    if (!email) {
      toast.error("Informe seu e-mail");
      return;
    }

    // Verificar Turnstile
    const siteKey = getTurnstileSiteKey();
    if (siteKey) {
      if (!turnstileWidgetId.current || !window.turnstile) {
        toast.error("Verificação de segurança não carregada. Recarregue a página.");
        return;
      }

      // Tentar obter token do ref primeiro (mais confiável)
      let turnstileToken = turnstileTokenRef.current;
      
      // Se não há token no ref, tentar obter do widget
      if (!turnstileToken) {
        turnstileToken = window.turnstile.getResponse(turnstileWidgetId.current);
      }
      
      // Se ainda não há token, resetar e aguardar o usuário completar novamente
      if (!turnstileToken) {
        toast.error("Por favor, complete a verificação de segurança");
        // Resetar widget para gerar novo token
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileTokenRef.current = null;
        return;
      }

      // Verificar token no servidor imediatamente (antes que expire)
      const verificationResult = await verifyTurnstileToken(turnstileToken);
      if (!verificationResult.success) {
        // Se o erro for timeout-or-duplicate, resetar e pedir nova verificação
        if (verificationResult.errorCodes?.includes("timeout-or-duplicate")) {
          toast.error("A verificação expirou. Por favor, complete a verificação novamente.");
          // Resetar widget para gerar novo token
          if (turnstileWidgetId.current && window.turnstile) {
            window.turnstile.reset(turnstileWidgetId.current);
            turnstileTokenRef.current = null;
          }
        } else {
          toast.error(verificationResult.error || "Verificação de segurança falhou. Tente novamente.");
          // Resetar widget para permitir nova tentativa
          if (turnstileWidgetId.current && window.turnstile) {
            window.turnstile.reset(turnstileWidgetId.current);
            turnstileTokenRef.current = null;
          }
        }
        return;
      }
      
      // Não limpar/resetar ainda - vamos fazer isso após a requisição (sucesso ou erro)
    }

    try {
      const { data, error } = await supabase.functions.invoke("send-password-reset-email", {
        body: { email },
      });

      if (error) throw error;

      setSubmitted(true);
      // Resetar widget do Turnstile após sucesso
      if (siteKey && turnstileWidgetId.current && window.turnstile) {
        turnstileTokenRef.current = null;
        window.turnstile.reset(turnstileWidgetId.current);
      }
      toast.success("Se o e-mail estiver cadastrado, você receberá um link de redefinição.");
    } catch (error) {
      console.error("Error requesting reset:", error);
      toast.error("Erro ao solicitar redefinição de senha");
      // Resetar widget do Turnstile em caso de erro
      if (siteKey && turnstileWidgetId.current && window.turnstile) {
        turnstileTokenRef.current = null;
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Preencha todos os campos");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("reset-password", {
        body: { token, new_password: newPassword },
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success("Senha redefinida com sucesso!");
      navigate("/auth?reset=true");
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Erro ao redefinir senha");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-auto w-auto" />
        </div>

        <Card className="shadow-medium">
          <CardContent className="p-8">
            {token ? (
              // Reset password form
              <>
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold">Definir nova senha</h1>
                  <p className="text-muted-foreground text-sm mt-2">
                    Digite sua nova senha abaixo.
                  </p>
                </div>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nova senha</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                    {isLoading ? "Redefinindo..." : "Redefinir senha"}
                  </Button>
                </form>
              </>
            ) : submitted ? (
              // Success message
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4">E-mail enviado!</h1>
                <p className="text-muted-foreground text-sm mb-6">
                  Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha. Verifique sua caixa de entrada e spam.
                </p>
                <Button variant="outline" onClick={() => navigate("/auth")} className="w-full">
                  Voltar ao login
                </Button>
              </div>
            ) : (
              // Request reset form
              <>
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold">Esqueceu sua senha?</h1>
                  <p className="text-muted-foreground text-sm mt-2">
                    Informe seu e-mail e enviaremos um link para redefinir sua senha.
                  </p>
                </div>
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">E-mail</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {getTurnstileSiteKey() ? (
                    <div className="flex flex-col items-center justify-center py-2 min-h-[65px]">
                      <div 
                        ref={turnstileRef} 
                        id="turnstile-widget-reset"
                        className="w-full flex justify-center"
                        style={{ minHeight: '65px' }}
                      />
                    </div>
                  ) : (
                    <div className="flex justify-center py-2 text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                      ⚠️ VITE_TURNSTILE_SITE_KEY não configurada no .env
                    </div>
                  )}

                  <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                    {isLoading ? "Enviando..." : "Enviar link de redefinição"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/auth")}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Voltar ao login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
